import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquarePlus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

import { apiDetail } from '../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  ['problem', 'Something is wrong'],
  ['idea', 'An idea'],
  ['question', 'A question'],
  ['praise', 'Something works well'],
];

/**
 * BILL-6 — tell Northline something from anywhere in the app. It reaches
 * staff with the company, the person and the page it was sent from.
 */
export default function FeedbackButton() {
  const { token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('problem');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.error('Tell us what happened'); return; }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/feedback`,
        { category, message, page_path: location.pathname },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Thank you. Northline has your feedback.');
      setOpen(false);
      setMessage('');
      setCategory('problem');
    } catch (e) {
      toast.error(apiDetail(e) || 'Could not send feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label="Send Feedback"
        title="Send Feedback"
        className="text-slate-600 dark:text-slate-400"
        data-testid="header-feedback-button"
      >
        <MessageSquarePlus className="w-5 h-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>Northline staff read every message.</DialogDescription>
          </DialogHeader>
          <label className="text-sm text-slate-700 dark:text-slate-300">
            What is it about?
            <select
              className="block mt-1 w-full h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-sm"
              value={category}
              onChange={e => setCategory(e.target.value)}
              aria-label="Feedback category"
            >
              {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-sm text-slate-700 dark:text-slate-300">
            Details
            <textarea
              className="block mt-1 w-full min-h-[120px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-2 text-sm"
              value={message}
              maxLength={5000}
              onChange={e => setMessage(e.target.value)}
              aria-label="Feedback details"
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={sending} data-testid="send-feedback">Send Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
